<template>
  <div class="financial-statement-item">
    <col-table :data="data" :pageSize="pageSize" :class="{ 'col-table--same': isSame, 'table-var': true }">
      <col-table-column label="日期" prop="endDate" :css="{ height: '50px' }">
        <template #header>
          <div class="date-header">
            <span>报告期<i class="iconfont icon-wenhao ml-4px" @click="popupVisible = true"></i></span>
            <span class="tongbi">
              {{ $t('同比')
              }}<span class="checkbox-wrap"><input type="checkbox" v-model="isSame" class="checkbox" /></span>
            </span>
          </div>
        </template>
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ scope.row.sortYear + scope.row.reportType }}</span>
            <span class="scale">{{ $t('同比') }}</span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="reportKind" label="报表种类">
        <template slot-scope="scope">{{
          $digitjs(reportKindMap[scope.row[formatAParams('reportKind')]]).isNull()
        }}</template>
      </col-table-column>
      <col-table-column prop="coverMonths" label="覆盖时期(月)">
        <template slot-scope="scope">{{ $digitjs(scope.row[formatAParams('coverMonths')]).isNull() }}</template>
      </col-table-column>
      <col-table-column prop="currency" label="报表货币">
        <template slot-scope="scope">{{ $digitjs(scope.row[formatAParams('currency')]).isNull() }}</template>
      </col-table-column>
      <col-table-column prop="operatingIncome" label="营业收入(营业额)" :css="mainDataStyle">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('operatingIncome')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('operatingIncomeYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('operatingIncomeYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="operatingExpenses" :label="`- ${$t('营业成本')}`">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('operatingExpenses')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('operatingExpensesYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('operatingExpensesYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="grossProfit" label="营业毛利" :css="mainDataStyle">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('grossProfit')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('grossProfitYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('grossProfitYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="grossProfitMargin" label="营业毛利率" :css="mainDataStyle">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('grossProfitMargin')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('grossProfitMarginYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('grossProfitMarginYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="generalAdminExpenses" :label="`- ${$t('行政开支')}`">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('generalAdminExpenses')]).convert() }}</span>
            <span
              :style="{ color: $digitjs(scope.row[formatAParams('generalAdminExpensesYoY')]).color() }"
              class="scale"
            >
              {{ $digitjs(scope.row[formatAParams('generalAdminExpensesYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="otherOperatingExpenses" :label="`- ${$t('其他营业支出')}`">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('otherOperatingExpenses')]).convert() }}</span>
            <span
              :style="{ color: $digitjs(scope.row[formatAParams('otherOperatingExpensesYoY')]).color() }"
              class="scale"
            >
              {{ $digitjs(scope.row[formatAParams('otherOperatingExpensesYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="totalOperatingExpenses" label="经营开支总额" :css="mainDataStyle">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('totalOperatingExpenses')]).convert() }}</span>
            <span
              :style="{ color: $digitjs(scope.row[formatAParams('totalOperatingExpensesYoY')]).color() }"
              class="scale"
            >
              {{ $digitjs(scope.row[formatAParams('totalOperatingExpensesYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="coreProfit" label="核心利润" :css="mainDataStyle">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('coreProfit')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('coreProfitYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('coreProfitYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="otherIncome" :label="`+ ${$t('其他收入')}`">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('otherIncome')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('otherIncomeYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('otherIncomeYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="otherNonOperatingItems" :label="`+ ${$t('其他非经营项目')}`">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('otherNonOperatingItems')]).convert() }}</span>
            <span
              :style="{ color: $digitjs(scope.row[formatAParams('otherNonOperatingItemsYoY')]).color() }"
              class="scale"
            >
              {{ $digitjs(scope.row[formatAParams('otherNonOperatingItemsYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="operatingProfit" label="营业利润" :css="mainDataStyle">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('operatingProfit')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('operatingProfitYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('operatingProfitYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="financeCost" :label="`- ${$t('财务成本')}`">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('financeCost')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('financeCostYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('financeCostYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="joinContrEntitiesAssociates" label="共同控制公司及联营公司">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('joinContrEntitiesAssociates')]).convert() }}</span>
            <span
              :style="{ color: $digitjs(scope.row[formatAParams('joinContrEntitiesAssociatesYoY')]).color() }"
              class="scale"
            >
              {{ $digitjs(scope.row[formatAParams('joinContrEntitiesAssociatesYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="profitBeforeTaxation" label="除税前溢利" :css="mainDataStyle">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('profitBeforeTaxation')]).convert() }}</span>
            <span
              :style="{ color: $digitjs(scope.row[formatAParams('profitBeforeTaxationYoY')]).color() }"
              class="scale"
            >
              {{ $digitjs(scope.row[formatAParams('profitBeforeTaxationYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="taxation" label="税项">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('taxation')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('taxationYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('taxationYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="profitThePeriod" label="本期间溢利" :css="mainDataStyle">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('profitThePeriod')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('profitThePeriodYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('profitThePeriodYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="ownersOfTheCom" label="本公司股本持有人应占">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('ownersOfTheCom')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('ownersOfTheComYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('ownersOfTheComYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="nonControllingInterests" label="非控股权益">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('nonControllingInterests')]).convert() }}</span>
            <span
              :style="{ color: $digitjs(scope.row[formatAParams('nonControllingInterestsYoY')]).color() }"
              class="scale"
            >
              {{ $digitjs(scope.row[formatAParams('nonControllingInterestsYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="netProfit" label="净利润(纯利)" :css="mainDataStyle">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('netProfit')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('netProfitYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('netProfitYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="netIncomeRatio" label="净利率" :css="mainDataStyle">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('netIncomeRatio')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('netIncomeRatioYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('netIncomeRatioYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="returnOnEquity" label="净资产收益率(ROE)" :css="mainDataStyle">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('returnOnEquity')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('returnOnEquityYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('returnOnEquityYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="returnOnTotalAssets" label="资产收益率(ROA)" :css="mainDataStyle">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('returnOnTotalAssets')]).convert() }}</span>
            <span
              :style="{ color: $digitjs(scope.row[formatAParams('returnOnTotalAssetsYoY')]).color() }"
              class="scale"
            >
              {{ $digitjs(scope.row[formatAParams('returnOnTotalAssetsYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="otherComprehensiveIncome" label="其他全面收入">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('otherComprehensiveIncome')]).convert() }}</span>
            <span
              :style="{ color: $digitjs(scope.row[formatAParams('otherComprehensiveIncomeYoY')]).color() }"
              class="scale"
            >
              {{ $digitjs(scope.row[formatAParams('otherComprehensiveIncomeYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="totalComprehensiveIncome" label="全面收入总额" :css="mainDataStyle">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('totalComprehensiveIncome')]).convert() }}</span>
            <span
              :style="{ color: $digitjs(scope.row[formatAParams('totalComprehensiveIncomeYoY')]).color() }"
              class="scale"
            >
              {{ $digitjs(scope.row[formatAParams('totalComprehensiveIncomeYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="basicEPS" label="财报每股基本盈利">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('basicEPS')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('basicEPSYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('basicEPSYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="depreciation" label="物业、机器及设备折旧">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('depreciation')]).convert() }}</span>
            <span :style="{ color: $digitjs(scope.row[formatAParams('depreciationYoY')]).color() }" class="scale">
              {{ $digitjs(scope.row[formatAParams('depreciationYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="depreciationAmortization" label="折旧及摊销">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('depreciationAmortization')]).convert() }}</span>
            <span
              :style="{ color: $digitjs(scope.row[formatAParams('depreciationAmortizationYoY')]).color() }"
              class="scale"
            >
              {{ $digitjs(scope.row[formatAParams('depreciationAmortizationYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="commonShareDividend" label="普通股股息">
        <template slot-scope="scope">
          <div class="value-wrap">
            <span class="value">{{ $digitjs(scope.row[formatAParams('commonShareDividend')]).convert() }}</span>
            <span
              :style="{ color: $digitjs(scope.row[formatAParams('commonShareDividendYoY')]).color() }"
              class="scale"
            >
              {{ $digitjs(scope.row[formatAParams('commonShareDividendYoY')]).prefix().unit('%').rounded() }}
            </span>
          </div>
        </template>
      </col-table-column>
      <col-table-column prop="taxationRate" label="税率(%)">
        <template slot-scope="scope">{{ $digitjs(scope.row[formatAParams('taxationRate')]).convert() }}</template>
      </col-table-column>
    </col-table>
    <RkPopup :visible.sync="popupVisible"><ReportSheetPrompt /></RkPopup>
  </div>
</template>

<script>
import RkPopup from '@/components/common/Popup.vue'
import ReportSheetPrompt from '@/components/common/ReportSheetPrompt.vue'
export default {
  components: { RkPopup, ReportSheetPrompt },
  props: ['data', 'reportKindMap', 'mainDataStyle', 'formatAParams'],
  data() {
    return {
      isSame: false,
      popupVisible: false
    }
  },
  computed: {
    pageSize() {
      return this.isSame ? 1 : 2
    }
  }
}
</script>
